import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Bell, MessageCircle, Users, TrendingUp, Loader2, BookOpen, Bookmark, Timer } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface NotificationPrefs {
  friend_started_reading: boolean;
  friend_reviewed: boolean;
  friend_saved_book: boolean;
  friend_level_ups: boolean;
  friend_focus_sessions: boolean;
  dino_duo_completed: boolean;
}

const notificationSettings = [
  {
    key: 'friend_started_reading' as keyof NotificationPrefs,
    title: 'Started Reading',
    description: 'When friends start reading a new book',
    icon: BookOpen,
  },
  {
    key: 'friend_reviewed' as keyof NotificationPrefs,
    title: 'Reviews',
    description: 'When friends review a book',
    icon: MessageCircle,
  },
  {
    key: 'friend_saved_book' as keyof NotificationPrefs,
    title: 'Saved to TBR',
    description: 'When friends add books to their reading list',
    icon: Bookmark,
  },
  {
    key: 'friend_level_ups' as keyof NotificationPrefs,
    title: 'Level Ups',
    description: 'When friends reach a new level',
    icon: TrendingUp,
  },
  {
    key: 'friend_focus_sessions' as keyof NotificationPrefs,
    title: 'Focus Sessions',
    description: 'When friends complete a focus session',
    icon: Timer,
  },
  {
    key: 'dino_duo_completed' as keyof NotificationPrefs,
    title: 'Dino Duo Completed',
    description: 'When you and a friend complete a Dino Duo together',
    icon: Users,
  },
];

export function NotificationPreferences() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPrefs>({
    friend_started_reading: true,
    friend_reviewed: true,
    friend_saved_book: true,
    friend_level_ups: true,
    friend_focus_sessions: true,
    dino_duo_completed: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        // Cast to any to handle new columns not yet in generated types
        const prefs = data as any;
        setPreferences({
          friend_started_reading: prefs.friend_started_reading ?? true,
          friend_reviewed: prefs.friend_reviewed ?? true,
          friend_saved_book: prefs.friend_saved_book ?? true,
          friend_level_ups: prefs.friend_level_ups ?? true,
          friend_focus_sessions: prefs.friend_focus_sessions ?? true,
          dino_duo_completed: prefs.dino_duo_completed ?? true,
        });
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notification preferences',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationPrefs) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
        }, { onConflict: 'user_id' });

      if (error) throw error;

      toast({
        title: 'Preferences saved',
        description: 'Your notification preferences have been updated',
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save notification preferences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <CardTitle>Notification Preferences</CardTitle>
        </div>
        <CardDescription>
          Choose which notifications you want to receive
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {notificationSettings.map((setting, index) => {
            const Icon = setting.icon;
            return (
              <div key={setting.key}>
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Icon className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="space-y-0.5">
                      <Label
                        htmlFor={setting.key}
                        className="text-base font-medium cursor-pointer"
                      >
                        {setting.title}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {setting.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={setting.key}
                    checked={preferences[setting.key]}
                    onCheckedChange={() => handleToggle(setting.key)}
                  />
                </div>
                {index < notificationSettings.length - 1 && (
                  <Separator className="mt-4" />
                )}
              </div>
            );
          })}
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
